import React from "react";
import { graphql, useLazyLoadQuery } from "react-relay";

import { faCheck } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cx from "classnames";

import { useDebouncedValue } from "~/lib/hooks";
import { SearchableSelector } from "~/ui/legacyFellowIndex";
import Menu from "~/ui/menu";

import type { ChannelSelectorQuery } from "~/__generated__/ChannelSelectorQuery.graphql";

import styles from "./ChannelSelector.module.css";

const channelsQuery = graphql`
	query ChannelSelectorQuery($query: String, $memberOnly: Boolean) {
		channels(excludePersonal: true, search: $query, memberOnly: $memberOnly) {
			id
			name
		}
	}
`;

interface ChannelSelectorProps {
	selected?: Pick<Channel, "id" | "name"> | null;
	onChange(channel: Channel | null): void;
	memberOnly?: boolean;
	className?: string;
}

export default function ChannelSelector({ selected, memberOnly, className, onChange }: ChannelSelectorProps) {
	const [searchQuery, setSearchQuery] = React.useState<string>("");
	const debouncedSearchQuery = useDebouncedValue(searchQuery, 350);

	const triggerContent = React.useMemo(() => {
		return <div className={styles.trigger}>{selected?.name ?? "Select a channel"}</div>;
	}, [selected]);

	const onClear = React.useCallback(() => {
		onChange(null);
	}, [onChange]);

	return (
		<SearchableSelector<string>
			triggerClassName={className}
			triggerContent={triggerContent}
			searchParam={searchQuery}
			value={selected?.id}
			onSearch={setSearchQuery}
			onClear={onClear}
			searchPlaceholder="Search"
			clearText="Clear channel"
		>
			<React.Suspense fallback={<span className={styles.loadingState}>Loading...</span>}>
				<ChannelsRenderer
					query={debouncedSearchQuery}
					memberOnly={memberOnly}
					selected={selected}
					onSelect={onChange}
				/>
			</React.Suspense>
		</SearchableSelector>
	);
}

export type Channel = ChannelSelectorQuery["response"]["channels"][0];
interface OptionProps {
	channel: Channel;
	selected: boolean;
	onSelect: (channel: Channel) => void;
}

export function Option({ channel, selected, onSelect }: OptionProps) {
	const onSelectChannel = React.useCallback(() => {
		onSelect(channel);
	}, [onSelect, channel]);

	return (
		<Menu.Item onSelect={onSelectChannel} className={cx({ [styles.selected]: selected })}>
			<span className={styles.channelName} title={channel.name}>
				{channel.name}
			</span>
			{selected && <FontAwesomeIcon icon={faCheck} />}
		</Menu.Item>
	);
}

interface ChannelsRendererProps {
	query: string;
	memberOnly?: boolean;
	selected?: Pick<Channel, "id" | "name"> | null;
	onSelect: (channel: Channel) => void;
}

function ChannelsRenderer({ query, memberOnly, selected, onSelect }: ChannelsRendererProps) {
	const data = useLazyLoadQuery<ChannelSelectorQuery>(
		channelsQuery,
		{ query, memberOnly },
		{ fetchPolicy: "store-and-network" },
	);

	return (
		<>
			{data.channels.length > 0 ? (
				data.channels.map(channel => (
					<Option key={channel.id} channel={channel} selected={selected?.id === channel.id} onSelect={onSelect} />
				))
			) : (
				<Menu.Item className={styles.noResults}>No channels found</Menu.Item>
			)}
		</>
	);
}
